import { NavLink, useNavigate } from "react-router-dom";
import {
  Home,
  Lock,
  AccountCircle,
  Logout,
  Menu as MenuIcon,
  Person as PersonIcon,
  Add as AddIcon,
  Explore as ExploreIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import {
  Button,
  AppBar,
  Toolbar,
  IconButton,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import { useAuth } from "../contexts/auth.context";
import { useProjectStore } from "../stores/project.store";
import { useState } from "react";
import BaseModal from "./modals/BaseModal";
import ProjectForm from "./forms/ProjectForm";

const logo = `${process.env.PUBLIC_URL}/assets/logo.png`;

export interface HeaderProps {
  className?: string;
}

const Header = ({ className = "" }: HeaderProps) => {
  const { auth, logout, isAuthenticated } = useAuth();
  const { setActiveTab } = useProjectStore();
  const { selectProject, refreshProjects } = useProjectStore();
  const navigate = useNavigate();
  const username = auth?.user || "User";
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState<null | HTMLElement>(
    null,
  );
  const [showCreateProjectModal, setShowCreateProjectModal] = useState(false);

  const handleLogoutClick = () => {
    logout();
  };

  const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMobileMenuAnchor(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuAnchor(null);
  };

  const handleCreateProjectClick = () => {
    setShowCreateProjectModal(true);
  };

  const handleProjectCreated = async (newProject: any) => {
    if (newProject) {
      selectProject(newProject);
      setActiveTab(newProject.project_name);
      refreshProjects();
    }
    setShowCreateProjectModal(false);
  };

  const handleCloseModal = () => {
    setShowCreateProjectModal(false);
  };

  return (
    <>
      <AppBar
        position="sticky"
        className={`${className} h-16 sm:h-16`}
        sx={{
          backgroundColor: "#f5f5f5",
          color: "text.primary",
          height: { xs: "56px", sm: "64px" },
          minHeight: { xs: "56px", sm: "64px" },
        }}
      >
        <Toolbar
          className="h-16 sm:h-16 min-h-16 sm:min-h-16"
          sx={{
            minHeight: { xs: "56px !important", sm: "64px !important" },
            height: { xs: "56px !important", sm: "64px !important" },
          }}
        >
          <div className="flex items-center">
            <IconButton
              edge="start"
              color="inherit"
              aria-label="home"
              onClick={() => navigate("/")}
              className="p-2 mr-2"
              sx={{ p: 1 }}
            >
              <img
                src={logo}
                alt="Logo"
                className="h-8 w-auto max-h-8 object-contain"
              />
            </IconButton>
            <Typography
              variant="h6"
              component="div"
              onClick={() => navigate("/")}
              className="cursor-pointer font-bold text-gray-800"
              sx={{
                fontSize: { xs: "1.1rem", sm: "1.25rem" },
                fontWeight: 700,
                color: "#1f2937",
              }}
            >
              SurvOm
            </Typography>
          </div>

          {/* Desktop Navigation - Hidden on mobile */}
          <div className="hidden sm:flex items-center gap-2 justify-end flex-1">
            {!isAuthenticated ? (
              <>
                {/* When NOT signed in: Home and Authentication */}
                <Button
                  component={NavLink}
                  to="/"
                  color="inherit"
                  startIcon={<Home />}
                  sx={{ textTransform: "none" }}
                >
                  Home
                </Button>
                <Button
                  component={NavLink}
                  to="/auth"
                  color="inherit"
                  startIcon={<Lock />}
                  sx={{ textTransform: "none" }}
                >
                  Authentication
                </Button>
              </>
            ) : (
              <>
                {/* When signed in: User info and Action Icons */}
                <div className="flex items-center gap-2 mr-2">
                  <PersonIcon />
                  <Typography variant="body2" className="text-gray-700">
                    {username}
                  </Typography>
                </div>
                <IconButton
                  color="inherit"
                  aria-label="create project"
                  onClick={handleCreateProjectClick}
                >
                  <AddIcon />
                </IconButton>
                <IconButton color="inherit" aria-label="explore">
                  <ExploreIcon />
                </IconButton>
                <IconButton
                  color="inherit"
                  aria-label="logout"
                  onClick={handleLogoutClick}
                >
                  <Logout />
                </IconButton>
              </>
            )}
          </div>

          {/* Mobile Hamburger Menu - Far Right */}
          <IconButton
            color="inherit"
            aria-label="menu"
            onClick={handleMobileMenuOpen}
            sx={{
              display: { xs: "block", sm: "none" },
              marginLeft: "auto",
            }}
          >
            <MenuIcon />
          </IconButton>

          {/* Mobile Full-Screen Menu */}
          <Menu
            anchorEl={mobileMenuAnchor}
            open={Boolean(mobileMenuAnchor)}
            onClose={handleMobileMenuClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            sx={{
              display: { xs: "block", sm: "none" },
              "& .MuiPaper-root": {
                position: "fixed !important",
                top: "56px !important",
                left: "0 !important",
                right: "0 !important",
                width: "100vw !important",
                height: "calc(100vh - 56px) !important",
                maxWidth: "100vw !important",
                borderRadius: "0 !important",
                margin: "0 !important",
                transform: "none !important",
                backgroundColor: "white",
                boxShadow: "none",
                border: "none",
                zIndex: 1300,
              },
            }}
          >
            {/* Mobile Menu Content */}
            <div className="flex flex-col h-full">
              {!isAuthenticated ? (
                <>
                  <MenuItem
                    onClick={() => {
                      navigate("/");
                      handleMobileMenuClose();
                    }}
                    sx={{
                      py: 2,
                      px: 3,
                      fontSize: "1.125rem",
                      borderBottom: "1px solid #f3f4f6",
                    }}
                  >
                    <Home sx={{ mr: 2, fontSize: 24 }} />
                    Home
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      navigate("/auth");
                      handleMobileMenuClose();
                    }}
                    sx={{
                      py: 2,
                      px: 3,
                      fontSize: "1.125rem",
                      borderBottom: "1px solid #f3f4f6",
                    }}
                  >
                    <Lock sx={{ mr: 2, fontSize: 24 }} />
                    Authentication
                  </MenuItem>
                </>
              ) : (
                <>
                  <MenuItem
                    disabled
                    sx={{
                      py: 2,
                      px: 3,
                      fontSize: "1.125rem",
                      borderBottom: "1px solid #f3f4f6",
                      color: "#6b7280",
                    }}
                  >
                    <AccountCircle sx={{ mr: 2, fontSize: 24 }} />
                    {username}
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      handleLogoutClick();
                      handleMobileMenuClose();
                    }}
                    sx={{
                      py: 2,
                      px: 3,
                      fontSize: "1.125rem",
                      borderBottom: "1px solid #f3f4f6",
                    }}
                  >
                    <Logout sx={{ mr: 2, fontSize: 24 }} />
                    Logout
                  </MenuItem>
                  <MenuItem
                    onClick={handleMobileMenuClose}
                    sx={{
                      py: 2,
                      px: 3,
                      fontSize: "1.125rem",
                      borderBottom: "1px solid #f3f4f6",
                    }}
                  >
                    <PersonIcon sx={{ mr: 2, fontSize: 24 }} />
                    Profile
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      handleCreateProjectClick();
                      handleMobileMenuClose();
                    }}
                    sx={{
                      py: 2,
                      px: 3,
                      fontSize: "1.125rem",
                      borderBottom: "1px solid #f3f4f6",
                    }}
                  >
                    <AddIcon sx={{ mr: 2, fontSize: 24 }} />
                    Create Project
                  </MenuItem>
                  <MenuItem
                    onClick={handleMobileMenuClose}
                    sx={{
                      py: 2,
                      px: 3,
                      fontSize: "1.125rem",
                      borderBottom: "1px solid #f3f4f6",
                    }}
                  >
                    <ExploreIcon sx={{ mr: 2, fontSize: 24 }} />
                    Explore
                  </MenuItem>
                  <MenuItem
                    onClick={handleMobileMenuClose}
                    sx={{
                      py: 2,
                      px: 3,
                      fontSize: "1.125rem",
                      borderBottom: "1px solid #f3f4f6",
                    }}
                  >
                    <RefreshIcon sx={{ mr: 2, fontSize: 24 }} />
                    Refresh
                  </MenuItem>
                </>
              )}
            </div>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Create Project Modal */}
      <BaseModal
        open={showCreateProjectModal}
        onClose={handleCloseModal}
        title="Create New Project"
      >
        <ProjectForm
          mode="create"
          onSubmit={handleProjectCreated}
          onCancel={handleCloseModal}
        />
      </BaseModal>
    </>
  );
};

export default Header;
